import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailsModalProps } from '../../../pages/dashboard/editModals';
import { Button, ButtonStyles, ButtonWidth, Checkbox,
  DatePicker, DropDown, Input, Theme, Tooltip, TooltipPreference, } from '../../components';
import { MLPVersion } from '../../components/Form/Input/Input';
import { customEventPropType } from '../../components/ImageEditor/types/propsType';
import { useAppSelector, useAPICall } from '../../../hooks';
import VideoEditor from '../../components/VideoEditor/VideoEditor';
import { InputSize, InputValidations } from '../../components/Form/Input/types/enums';
import { DynamicObjectInterface } from '../../../sdk/type';
import AttachmentService from '../../../sdk/services/attachmentService';
import { getTranslatedMessage, LoggerService, validateAttachment, VIDEO_FILE_TYPES } from '../../utils';
import { useAdminToast } from '../../components/AdminToast/AdminToast';
import styles from './assessmentDetail.module.scss';

interface FileWithUrl {
  file: File;
  url: string;
  photoId?: number;
}
interface ProgressItem {
  file: FileWithUrl;
  percentage: number;
}

export const AssessmentDetail = ({
  selectedTab = 'weightIn',
  data,
  formData,
  handleOnChange = () => {},
  onSave,
  validity = 60,
  postAssessmentReminderDays = 0,
  onCancel,
}: DetailsModalProps) => {
  const { t } = useTranslation();
  const { userInfo } = useAppSelector(state => state.auth);
  const apiCall = useAPICall();
  const currentDate = new Date();
  const regex = /^\d*\.?\d{0,2}$/;
  // eslint-disable-next-line no-unused-vars
  const [files, setFiles] = useState<{ [key: string]: FileWithUrl[] }>({});
  const [progress, setProgress] = useState<{ [key: string]: ProgressItem[] }>({});
  const [weightDetails, setWeightDetails] = useState(
    selectedTab === 'weightIn' ? formData?.weightIn : formData?.weightOut,
  );
  const [isReadOnly, setIsReadOnly] = useState(
    selectedTab === 'weightIn' ? !!data?.weightIn?.weightedDate : !!data?.weightOut?.weightedDate,
  );
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const { adminToast } = useAdminToast();

  // Resolve the indicator list for this assessment. MULTI-mode campaigns scope
  // the assessment indicators to the participant's enrolled sub-challenge
  // (configJson.activities.subChallenges[].rules[4].subKpiCode); everything else
  // falls back to the base participant.rules[4].subKpiCode.
  const configJson = data?.campaign?.configJson;
  const participantSubKpiCode = configJson?.activities?.participant?.rules?.[4]?.subKpiCode ?? [];
  const enrolledSubChallengeId = data?.subChallengeId;
  const subChallengeSubKpiCode =
    configJson?.campaignMode === 'MULTI' && enrolledSubChallengeId
      ? configJson?.activities?.subChallenges?.find(
          (sc: DynamicObjectInterface) => sc.subChallengeId === enrolledSubChallengeId,
        )?.rules?.[4]?.subKpiCode
      : undefined;
  const subKpiList: DynamicObjectInterface[] = subChallengeSubKpiCode ?? participantSubKpiCode;

  // Resolve the file-upload config for this assessment, mirroring the indicator
  // resolution above. MULTI-mode campaigns scope uploads to the enrolled
  // sub-challenge (configJson.activities.subChallenges[].fileUploader); everything
  // else falls back to the base participant.fileUploader.
  const participantFileUploader = configJson?.activities?.participant?.fileUploader ?? [];
  const subChallengeFileUploader =
    configJson?.campaignMode === 'MULTI' && enrolledSubChallengeId
      ? configJson?.activities?.subChallenges?.find(
          (sc: DynamicObjectInterface) => sc.subChallengeId === enrolledSubChallengeId,
        )?.fileUploader
      : undefined;
  const fileUploaderList: DynamicObjectInterface[] = subChallengeFileUploader ?? participantFileUploader;

  // Uploads flagged postAssessmentOnly are hidden (and not required) in the
  // pre-assessment (weightIn) tab; they only appear during post-assessment.
  const visibleFileUploaderList: DynamicObjectInterface[] =
    selectedTab === 'weightIn'
      ? fileUploaderList.filter((file: DynamicObjectInterface) => file.postAssessmentOnly !== 1)
      : fileUploaderList;

  // Test devices configured for the enrolled sub-challenge. When present, the
  // participant must pick a device and only that device's indicator variants apply.
  const testDevices: DynamicObjectInterface[] =
    (configJson?.campaignMode === 'MULTI' && enrolledSubChallengeId
      ? configJson?.activities?.subChallenges?.find(
          (sc: DynamicObjectInterface) => sc.subChallengeId === enrolledSubChallengeId,
        )?.testDevices
      : undefined) ?? [];
  const hasTestDevices = testDevices.length > 0;

  // Device is locked across pre/post: post-assessment always uses the device chosen
  // at pre-assessment. Pre-assessment uses its own (editable) selection.
  const lockedDevice = data?.weightIn?.machine ?? formData?.weightIn?.machine;
  const selectedDevice =
    selectedTab === 'weightOut' ? lockedDevice : weightDetails?.machine ?? lockedDevice;

  // Show only the indicators for the selected device (untagged indicators always show).
  const visibleSubKpiList: DynamicObjectInterface[] = hasTestDevices
    ? subKpiList.filter((i: DynamicObjectInterface) => !i.testDevice || i.testDevice === selectedDevice)
    : subKpiList;

  // Pre-assessment device dropdown is locked once submitted; post is always locked.
  const isDeviceLocked = isReadOnly || selectedTab === 'weightOut';

  // Post-assessment date must be STRICTLY AFTER the pre-assessment date. The
  // DatePicker has no minDate prop, so disable the pre-assessment date and every day
  // before it within the picker's selectable window (today-14 .. today). Dates are
  // normalised to local midnight to match the calendar's comparison.
  const preAssessmentDate: Date | null =
    selectedTab === 'weightOut' && data?.weightIn?.weightedDate ? new Date(data.weightIn.weightedDate) : null;
  const postAssessmentDisabledDates: Date[] = (() => {
    if (!preAssessmentDate) return [];
    const preMidnight = new Date(preAssessmentDate);
    preMidnight.setHours(0, 0, 0, 0);
    // Walk back from the pre-assessment day; 14 covers the picker's disabledPastNDates
    // window, +1 buffer. Every day <= pre-assessment is disabled (post must be later).
    const dates: Date[] = [];
    for (let i = 0; i <= 15; i++) {
      const d = new Date(preMidnight);
      d.setDate(d.getDate() - i);
      dates.push(d);
    }
    return dates;
  })();

  useEffect(() => {
    // console.log('selectedTab', selectedTab);
    // console.log('formData', selectedTab === 'weightIn' ? formData?.weightIn : formData?.weightOut);
    setWeightDetails(selectedTab === 'weightIn' ? formData?.weightIn : formData?.weightOut);
  }, [formData, selectedTab]);

  useEffect(() => {
    let readOnly = false;

    if (selectedTab === 'weightIn') {
      setProgress({});
      readOnly = !!data?.weightIn?.weightedDate;
      setTitle(t('dashboard.my-progress.preAssessmentTitle'));
      setSubTitle(readOnly ? '' : `${t('dashboard.my-progress.preAssessmentDesc')}`);
    } else if (selectedTab === 'weightOut') {
      setProgress({});
      const weightInDate = data?.weightIn?.weightedDate ? new Date(data.weightIn.weightedDate) : null;

      if (weightInDate) {
        if (!data?.weightOut?.weightedDate && data?.teamStartDate) {
          const validityDate = new Date(
            new Date(data?.teamStartDate)?.getTime() + (validity - postAssessmentReminderDays) * 24 * 60 * 60 * 1000,
          );
          readOnly = currentDate < validityDate;
          setTitle(t('dashboard.my-progress.postAssessmentTitle'));
          setSubTitle(
            readOnly
              ? `${t('dashboard.my-progress.postAssessmentNotAvailableDesc1')} ${
                  validity - postAssessmentReminderDays
                } ${t('dashboard.my-progress.postAssessmentNotAvailableDesc2')}.`
              : t('dashboard.my-progress.postAssessmentAvailableDesc'),
          );
        } else {
          setTitle(t('dashboard.my-progress.postAssessmentTitle'));
          readOnly = true;
        }
      } else {
        readOnly = true;
        setTitle(t('dashboard.my-progress.postAssessmentTitle'));
        setSubTitle(
          `${t('dashboard.my-progress.postAssessmentNotAvailableDesc1')} ${validity - postAssessmentReminderDays} ${t(
            'dashboard.my-progress.postAssessmentNotAvailableDesc2',
          )}.`,
        );
      }
    }

    setIsReadOnly(readOnly);
  }, [data, selectedTab, validity]);

  const handleFile = async (e: customEventPropType, fieldCode: string) => {
    // Initialize or reset progress for the specific field
    setProgress(prev => ({ ...prev, [fieldCode]: [] }));

    // Assessment proof uploads also allow video/audio (MP4, MOV, WAV) on top of image/pdf.
    // Images/PDF cap at 10MB; video/audio clips are larger, so they cap at 30MB.
    const res = validateAttachment(e, t, 10, VIDEO_FILE_TYPES, 30); // Validate files
    if (res?.status) {
      const selectedFiles = e.files;
      if (selectedFiles) {
        // Map the selected files to a new file structure with URL
        const newFiles = Object.entries(selectedFiles).map(([_, file]) => ({
          file,
          url: URL.createObjectURL(file),
        }));

        // Simulate the upload progress for each file
        await new Promise<void>(resolve => {
          const interval = setInterval(() => {
            setProgress(prev => {
              const currentProgress = prev[fieldCode] ?? [];
              const updatedProgress = [...currentProgress];

              newFiles.forEach((file, idx) => {
                const diff = Math.random() * 70;
                updatedProgress[idx] = {
                  file: file,
                  percentage: Math.min((updatedProgress[idx]?.percentage ?? 0) + diff, 100),
                };
              });

              // Check if all files have reached 100% progress
              if (updatedProgress.every(e => e.percentage === 100)) {
                clearInterval(interval);
                resolve(); // Resolve when all progress is 100%
              }

              return {
                ...prev,
                [fieldCode]: updatedProgress,
              };
            });
          }, 500); // Update progress every 500ms
        });

        // Update the file state with the newly selected files for the specific field
        setFiles(prevFiles => {
          const updatedFiles = {
            ...prevFiles,
            [fieldCode]: [...(prevFiles[fieldCode] ?? []), ...newFiles],
          };
          handleOnChange(updatedFiles[fieldCode], selectedTab, 'file', fieldCode); // Update formData for the specific field
          return updatedFiles;
        });
      }
    } else {
      // If validation fails, show an error message
      adminToast({
        message: getTranslatedMessage(t, res?.message ?? ''),
        theme: Theme.ERROR,
      });
    }
  };

  const handleDelete = (index: number, fieldCode: string) => {
    setFiles(prevFiles => {
      const updatedFiles = (prevFiles[fieldCode] ?? []).filter((_, i) => i !== index);
      handleOnChange(updatedFiles, selectedTab, 'file', fieldCode);
      return {
        ...prevFiles,
        [fieldCode]: updatedFiles,
      };
    });

    setProgress(prevProgress => {
      const updatedProgress = (prevProgress[fieldCode] ?? []).filter((_, i) => i !== index);
      return {
        ...prevProgress,
        [fieldCode]: updatedProgress,
      };
    });
  };

  const handleDownload = async (photoId: number) => {
    try {
      const response = await apiCall(AttachmentService.downloadSelected, { recId: photoId });

      if (response.status === 200 && !response.data?.data?.message) {
        const blob = response.data as Blob;
        const link = document.createElement('a');

        const extension = blob.type.split('/')[1] || 'bin';
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `result.${extension}`); // e.g., result.jpeg

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      } else {
        adminToast({
          message: response?.data?.data?.message,
          theme: Theme.ERROR,
        });
      }
    } catch (error) {
      LoggerService.log('Error downloading attachment', error);
    }
  };

  const isAssessmentComplete = (detail: DynamicObjectInterface) => {
    // When devices are configured, a device must be chosen and only its indicators count.
    if (hasTestDevices && !selectedDevice) {
      return false;
    }

    const allIndicatorsFilled = visibleSubKpiList.every((kpi: DynamicObjectInterface) => {
      if (kpi.disablePreAssessment === 1) {
        return true;
      }
      const key = kpi.subKpiCode;
      const value = detail.otherIndicators?.[key];
      return value !== undefined && value !== null && value !== '';
    });

    const allIndicatorsValid = Array.isArray(visibleSubKpiList)
      ? visibleSubKpiList.every((kpi: DynamicObjectInterface) => {
          if (kpi.disablePreAssessment === 1) {
            return true; 
          }
          const key = kpi.subKpiCode;
          const value = detail?.otherIndicators?.[key];

          const isValid = (val?: string) => {
            if (!val) return true;
            const num = parseFloat(val);
            return (
              (!kpi.indicatorMin || kpi.indicatorMin === undefined || num >= parseFloat(kpi.indicatorMin)) &&
              (!kpi.indicatorMin || kpi.indicatorMax === undefined || num <= parseFloat(kpi.indicatorMax))
            );
          };

          return isValid(value);
        })
      : true;

    const isCheckboxValid = detail.checkbox === true;

    const requiredUploader = visibleFileUploaderList;

    const isFileValid = requiredUploader.every((file: DynamicObjectInterface) => {
      // Loop through each expected file input based on numberOfFile
      return Array.from({ length: file.numberOfFile }).every((_, i) => {
        const files = detail?.file?.[`${file.fieldCode}_${i}`];
        return Array.isArray(files) && files.length > 0;
      });
    });

    // Post-assessment date must be strictly after the pre-assessment date. The date
    // picker already blocks earlier/equal days; this guards a pre-filled value too.
    let isDateAfterPre = true;
    if (selectedTab === 'weightOut' && preAssessmentDate && detail?.weightedDate) {
      const post = new Date(detail.weightedDate);
      post.setHours(0, 0, 0, 0);
      const pre = new Date(preAssessmentDate);
      pre.setHours(0, 0, 0, 0);
      isDateAfterPre = post.getTime() > pre.getTime();
    }

    return (
      detail?.weightedDate && allIndicatorsFilled && isCheckboxValid && isFileValid && allIndicatorsValid && isDateAfterPre
    );
  };

  const isIndicatorValueValid = (value?: string, min?: string, max?: string): boolean => {
    if (!value ) return true;
    const num = parseFloat(value);
    return (
      (!min || min === undefined || num >= parseFloat(min)) && (!max || max === undefined || num <= parseFloat(max))
    );
  };

  return (
    <div className={styles['assessment-detail']}>
      <div className={styles['assessment-detail-title-container']}>
        <div className={styles['assessment-detail-title']}>{title}</div>
        <div className={styles['assessment-detail-subtitle']}>{subTitle}</div>
      </div>

      <div className={styles['assessment-detail-content']}>
        <div className={styles['assessment-detail-content-item']}>
          <div className={styles['assessment-detail-content-item-label']}>{t('dashboard.my-progress.input.name')}</div>
          <div className={styles['assessment-detail-content-item-semicolon']}>:</div>
          <div className={styles['assessment-detail-content-item-input']}>{formData?.name}</div>
        </div>
        <div className={styles['assessment-detail-content-item']}>
          <div className={styles['assessment-detail-content-item-label']}>
            {selectedTab === 'weightIn'
              ? `${t('dashboard.my-progress.preAssessment')}`
              : `${t('dashboard.my-progress.postAssessment')}`}{' '}
            {t('dashboard.my-progress.input.date')}
            <span className={styles['assessment-detail-content-item-mandatory']}>*</span>
          </div>
          <div className={styles['assessment-detail-content-item-semicolon']}>:</div>
          <div className={styles['assessment-detail-content-item-input']}>
            <DatePicker
              inputContainerHeight="36px"
              showDateContainer
              calendarHeading={`${
                selectedTab === 'weightIn'
                  ? t('dashboard.my-progress.preAssessment')
                  : t('dashboard.my-progress.postAssessment')
              } ${t('dashboard.my-progress.input.date')}`}
              containerWidth="450px"
              value={weightDetails?.weightedDate ? new Date(weightDetails.weightedDate) : undefined}
              onChange={date => {
                handleOnChange(date?.toISOString() ?? '', selectedTab, 'weightedDate');
              }}
              inputSize={InputSize.SMALL}
              contentHeight={'77vh'}
              disabled={isReadOnly}
              // Center the calendar over the modal via fixed positioning. The input
              // sits in the right column, so the default below-input popup overflowed
              // the modal edge; fixed + translate keeps it fully visible and centered,
              // and escapes the modal's scroll clipping.
              calendarContainerStyling={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
              }}
              disabledFutureDates={true}
              disabledPastNDates={selectedTab === 'weightIn' ? 30 : 14}
              // Post-assessment: block the pre-assessment day and earlier so the
              // selected date is strictly after pre-assessment.
              disabledDates={postAssessmentDisabledDates}
            />
          </div>
        </div>
        {hasTestDevices && (
          <div className={styles['assessment-detail-content-item']}>
            <div className={styles['assessment-detail-content-item-label']}>
              {t('dashboard.my-progress.testDeviceLabel')}
              <span className={styles['assessment-detail-content-item-mandatory']}>*</span>
            </div>
            <div className={styles['assessment-detail-content-item-semicolon']}>:</div>
            <div className={styles['assessment-detail-content-item-input']}>
              <DropDown
                name="assessmentTestDevice"
                placeholder={t('dashboard.my-progress.testDevicePlaceholder')}
                value={testDevices.find(d => d.code === selectedDevice)?.name ?? ''}
                mlpVersion={MLPVersion.ONE}
                data={testDevices}
                disabled={isDeviceLocked}
                configData={{
                  label: 'name',
                  onSelect: (selectedOption: DynamicObjectInterface) => {
                    handleOnChange(selectedOption.code, selectedTab, 'machine');
                  },
                }}
              />
            </div>
          </div>
        )}
        {visibleSubKpiList?.map(
          (i: DynamicObjectInterface) => {
            const value = weightDetails?.otherIndicators?.[i.subKpiCode];
            const isValid = isIndicatorValueValid(value, i.indicatorMin, i.indicatorMax);

            return (
              <div className={styles['assessment-detail-content-item']}>
                <div className={styles['assessment-detail-content-item-label']}>
                  <span className={styles['assessment-detail-content-item-tooltip']}>
                  {i.indicatorName} ({i.indicatorMeasurementUnit})
                  { (selectedTab === 'weightIn' && i.disablePreAssessment === 1) ? (
                      <Tooltip
                      message={t('dashboard.my-progress.noPreAssessmentTooltip')}
                      preference={TooltipPreference.TOP_CENTER}
                      className={styles['input-tool-tip']}
                      iconName="circle-exclamation"
                    />
                  ) : (
                    <span className={styles['assessment-detail-content-item-mandatory']}>*</span>
                  )}
                  </span>
                </div>
                <div className={styles['assessment-detail-content-item-semicolon']}>:</div>
                <div className={styles['assessment-detail-content-item-input']}>
                  <Input
                    name={`${i.indicatorName} (${i.indicatorMeasurementUnit})`}
                    value={value ?? ''}
                    mlpVersion={MLPVersion.ONE}
                    onChange={e => {
                      const inputValue = e.target.value;
                      if (regex.test(inputValue)) {
                        handleOnChange(e.target.value, `${selectedTab}`, `otherIndicators`, `${i.subKpiCode}`);
                      }
                    }}
                    inputSize={InputSize.SMALL}
                    placeholder={i.indicatorPlaceholder}
                    disabled={isReadOnly || (selectedTab === 'weightIn' && i.disablePreAssessment === 1)}
                    status={isValid ? InputValidations.DEFAULT : InputValidations.INVALID}
                    message={isValid ? '' : t('dashboard.my-progress.assessmentInputValidation')}
                  />
                </div>
              </div>
            );
          },
        )}
        <div className={styles['assessment-detail-content-item']}>
          <div className={styles['assessment-detail-content-item-label']}>{`${t(
            'dashboard.my-progress.input.remarks',
          )}`}</div>
          <div className={styles['assessment-detail-content-item-semicolon']}>:</div>
          <div className={styles['assessment-detail-content-item-input']}>
            <Input
              name={`${t('dashboard.my-progress.input.remarks')}`}
              value={weightDetails?.remarks ? weightDetails?.remarks : ''}
              mlpVersion={MLPVersion.ONE}
              onChange={e => handleOnChange(e.target.value, selectedTab, 'remarks')}
              inputSize={InputSize.SMALL}
              disabled={isReadOnly}
            />
          </div>
        </div>
      </div>

      <div className={styles['assessment-detail-upload']}>
        <div>
          {visibleFileUploaderList.map((file: DynamicObjectInterface) => (
            <div key={file.fieldCode} className={styles['assessment-detail-upload-content-item']}>
              {(selectedTab === 'weightIn' || selectedTab === 'weightOut' || !isReadOnly) && (
                <div className={styles['assessment-detail-upload-content-item-label']}>
                  {file.fieldName}
                  {/* Every visible uploader is mandatory (Confirm stays disabled until
                      each has a file — see isAssessmentComplete), so mark it required. */}
                  <span className={styles['assessment-detail-content-item-mandatory']}>*</span>:
                </div>
              )}
              <div className={styles['video-editor-wrapper']}>
                {Array.from({ length: file.numberOfFile }).map((_, i) => (
                  <VideoEditor
                    key={i}
                    onFileUpload={e => handleFile(e, `${file.fieldCode}_${i}`)}
                    accept="image/*, application/pdf, video/mp4, video/quicktime, audio/wav, .mp4, .mov, .wav"
                    fileTypeLabel={t('dashboard.my-progress.allowedFileType')}
                    multiple={true}
                    files={weightDetails?.file?.[`${file.fieldCode}_${i}`] ?? []}
                    onDelete={index => handleDelete(index, `${file.fieldCode}_${i}`)}
                    handleDownload={photoId => handleDownload(photoId)}
                    disabled={isReadOnly}
                    progress={progress[`${file.fieldCode}_${i}`] ?? []}
                    dragCTALabel={t('dashboard.my-progress.dragAndDrop')}
                    connectorLabel={t('dashboard.my-progress.or')}
                    uploadCTALabel={t('dashboard.my-progress.selectFileToUpload')}
                    addAnotherFile={t('dashboard.my-progress.addAnotherFile')}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {!isReadOnly && visibleFileUploaderList.length > 0 && (
          <div className={styles['video-detail-title']}>
            <div>{t('dashboard.my-progress.assessmentUploadFileDesc')}</div>
            <div>{t('dashboard.my-progress.assessmentUploadFileSizeDesc')}</div>
          </div>
        )}
      </div>

      {!isReadOnly && (
        <>
          <div>
            <Checkbox
              key={`checkbox_${selectedTab}`}
              id={`checkbox_${selectedTab}`}
              theme={Theme.BLACK}
              checked={weightDetails?.checkbox}
              onChange={e => handleOnChange(!weightDetails?.checkbox, selectedTab, 'checkbox')}
              text={`${t('dashboard.my-progress.assessmentCheckboxDesc')}`}
            />
          </div>
          <div className={styles['assessment-detail-submission-section']}>
            <div className={styles['assessment-detail-submission-section-title']}>{`${t(
              'dashboard.my-progress.submissionFor',
            )}:`}</div>
            <div className={styles['assessment-detail-submission-section-label']}>{`${t(
              'dashboard.my-progress.submissionName',
            )}: ${userInfo?.name}`}</div>
            <div className={styles['assessment-detail-submission-section-label']}>{`${t(
              'dashboard.my-progress.submissionABOId',
            )}: ${userInfo?.loginId}`}</div>
          </div>
          <div className={styles['assessment-detail-buttons']}>
            <Button
              title={`${t('dashboard.my-progress.button.cancel')}`}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.GREEN}
              onClick={onCancel}
              buttonWidth={ButtonWidth.FULL_WIDTH}
            />
            <Button
              title={`${t('dashboard.my-progress.button.confirm')}`}
              onClick={() => onSave(formData)}
              disabled={!weightDetails || !isAssessmentComplete(weightDetails)}
              buttonWidth={ButtonWidth.FULL_WIDTH}
              theme={Theme.GREEN}
            />
          </div>
        </>
      )}
    </div>
  );
};
